import React, {useState, useEffect, useMemo} from 'react'
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap'
import {toMoneyFormat} from '../../../common/helpers/stringHelper'
import {
  collectChildSystemFeeChargeRows,
  collectParentWorkOrderChargeRows,
  filterPendingCharges,
  getChildWorkOrderChargesForQuote,
  quoteCostSellBreakdownWithCharges,
  quoteCostSellWithCharges,
  quoteLineCostSell,
  sumChargeValues,
  sumChargeValuesForCostBudget,
} from '../../quotes/utils/quoteAmountsWithCharges'
import {fetchParentWorkOrderQuotesBundle} from '../../quotes/utils/parentWorkOrderQuotesBundle'

interface Props {
  handleClose: () => void
  show: boolean
  parentWorkOrderId: any
  parentWorkOrderDisplay?: string
  refreshData: boolean
  updateParentProjectCost?: (sum: any) => void
}

/** Same rules as quote line labels: number, then WO-id, else em dash. */
export function formatWorkOrderDisplay(wo: any): string {
  if (wo?.workOrderNumber) return String(wo.workOrderNumber)
  if (wo?.workOrderNumberId != null && wo.workOrderNumberId !== '')
    return `WO-${wo.workOrderNumberId}`
  return '—'
}

const TABLE_COL_SPAN = 6

function expandGroupKey(group: any, index: number) {
  return String(group.quoteStatusId ?? group.quoteStatus?.id ?? group.quoteStatus?.name ?? index)
}

function computeMargin(cost: number, sell: number): {margin: number; marginPct: number | null} {
  const margin = sell - cost
  const marginPct = cost !== 0 ? (margin / cost) * 100 : null
  return {margin, marginPct}
}

function formatMarginPercent(pct: number | null): string {
  if (pct === null) return '—'
  return `${pct.toFixed(1)}%`
}

function quotesForGroup(group: any): any[] {
  const statusName = (group.quoteStatus?.name || '').toLowerCase()
  if (statusName === 'submitted') return group.submittedQuotes || []
  if (statusName === 'approved') return group.approvedQuotes || []
  if (statusName === 'cancelled') return group.cancelledQuotes || []
  const sq = group.submittedQuotes || []
  const aq = group.approvedQuotes || []
  const cq = group.cancelledQuotes || []
  if (sq.length) return sq
  if (aq.length) return aq
  if (cq.length) return cq
  return []
}

function sumGroupCostSellWithCharges(
  group: any,
  fullById: Map<string, any>,
  chargesList: any[],
  parentWorkOrderId: string
): {cost: number; sell: number} {
  const statusName = group.quoteStatus?.name
  return quotesForGroup(group).reduce(
    (acc, q) => {
      const full = fullById.get(String(q.id)) ?? q
      const {cost, sell} = quoteCostSellWithCharges(
        full,
        chargesList,
        parentWorkOrderId,
        statusName
      )
      return {cost: acc.cost + cost, sell: acc.sell + sell}
    },
    {cost: 0, sell: 0}
  )
}

/** Aligns with Quote Summary: submitted pending pool once; approved = lines + non-system child charges + deduped parent + child system fees (each once). */
function sumGroupCostSellAligned(
  group: any,
  fullById: Map<string, any>,
  chargesList: any[],
  parentWorkOrderId: string
): {cost: number; sell: number} {
  const statusLower = (group.quoteStatus?.name || '').toLowerCase()
  const quotes = quotesForGroup(group).map((q) => fullById.get(String(q.id)) ?? q)

  if (statusLower === 'submitted') {
    let lineCost = 0
    let lineSell = 0
    for (const q of quotes) {
      const {cost, sell} = quoteLineCostSell(q)
      lineCost += cost
      lineSell += sell
    }
    const pending = filterPendingCharges(chargesList, parentWorkOrderId)
    return {
      cost: lineCost + sumChargeValuesForCostBudget(pending),
      sell: lineSell + sumChargeValues(pending),
    }
  }

  if (statusLower === 'approved') {
    let lineCost = 0
    let lineSell = 0
    let childCost = 0
    let childSell = 0
    for (const q of quotes) {
      const lines = quoteLineCostSell(q)
      lineCost += lines.cost
      lineSell += lines.sell
      const childWoCharges = getChildWorkOrderChargesForQuote(q, chargesList, parentWorkOrderId)
      childCost += sumChargeValuesForCostBudget(childWoCharges)
      childSell += sumChargeValues(childWoCharges)
    }
    const parentRows = collectParentWorkOrderChargeRows(quotes, chargesList, parentWorkOrderId)
    const childFeeRows = collectChildSystemFeeChargeRows(quotes, chargesList, parentWorkOrderId)
    const bandCharges = [...parentRows.map((r) => r.charge), ...childFeeRows.map((r) => r.charge)]
    return {
      cost: lineCost + childCost + sumChargeValuesForCostBudget(bandCharges),
      sell: lineSell + childSell + sumChargeValues(bandCharges),
    }
  }

  return sumGroupCostSellWithCharges(group, fullById, chargesList, parentWorkOrderId)
}

function sumGroupLineCostSell(
  group: any,
  fullById: Map<string, any>,
  chargesList: any[],
  parentWorkOrderId: string
): {lineCost: number; lineSell: number} {
  const statusName = group.quoteStatus?.name
  return quotesForGroup(group).reduce(
    (acc, q) => {
      const full = fullById.get(String(q.id)) ?? q
      const b = quoteCostSellBreakdownWithCharges(full, chargesList, parentWorkOrderId, statusName)
      return {lineCost: acc.lineCost + b.lineCost, lineSell: acc.lineSell + b.lineSell}
    },
    {lineCost: 0, lineSell: 0}
  )
}

/**
 * Quote row totals scoped to **this quote’s own work order** only: estimate lines plus, for approved
 * only, processed charges on that WO excluding tenant/system fees (they appear in the Charges band).
 * Omits submitted parent WO pending pools and parent WO charges.
 */
function quoteChildWorkOrderTotals(
  quoteFull: any,
  chargesList: any[],
  parentWorkOrderId: string,
  quoteStatusName: string | undefined
): {cost: number; sell: number} {
  const {cost: lineCost, sell: lineSell} = quoteLineCostSell(quoteFull)
  const statusLower = (quoteStatusName || quoteFull?.quoteStatus?.name || '').toLowerCase()
  if (statusLower !== 'approved') {
    return {cost: lineCost, sell: lineSell}
  }
  const childWoCharges = getChildWorkOrderChargesForQuote(quoteFull, chargesList, parentWorkOrderId)
  const chargeCost = sumChargeValuesForCostBudget(childWoCharges)
  const chargeSell = sumChargeValues(childWoCharges)
  return {cost: lineCost + chargeCost, sell: lineSell + chargeSell}
}

/** Parent/group row Sell/charge: approved = deduped parent WO charges only; submitted = pending pool once; cancelled/other = null (show em dash). */
function sumGroupParentWoChargeSell(
  group: any,
  fullById: Map<string, any>,
  chargesList: any[],
  parentWorkOrderId: string
): number | null {
  const statusLower = (group.quoteStatus?.name || '').toLowerCase()
  if (statusLower === 'submitted') {
    return sumChargeValues(filterPendingCharges(chargesList, parentWorkOrderId))
  }
  if (statusLower === 'approved') {
    const quotes = quotesForGroup(group).map((q) => fullById.get(String(q.id)) ?? q)
    return collectParentWorkOrderChargeRows(quotes, chargesList, parentWorkOrderId).reduce(
      (s, row) => s + (Number(row.charge?.value) || 0),
      0
    )
  }
  return null
}

function totalForStatusGroup(
  groups: any[],
  status: 'submitted' | 'approved',
  fullById: Map<string, any>,
  chargesList: any[],
  parentWorkOrderId: string,
  side: 'cost' | 'sell'
): number {
  const g = groups.find((x) => (x.quoteStatus?.name || '').toLowerCase() === status)
  if (!g) return 0
  const {cost, sell} = sumGroupCostSellAligned(g, fullById, chargesList, parentWorkOrderId)
  return side === 'sell' ? sell : cost
}

function quoteWorkOrderLabel(quote: any): string {
  return formatWorkOrderDisplay(quote?.workOrder)
}

function chargeRowLabel(charge: any): string {
  const name = String(charge?.name ?? '').trim()
  return name || 'Charge'
}

function GroupStatusCell({group}: {group: any}) {
  return (
    <div className='d-flex align-items-center rounded p-5'>
      {group.quoteStatus?.color && (
        <span
          className='bullet bullet-vertical h-30px'
          style={{background: group.quoteStatus.color}}
        />
      )}
      <div
        className='flex-grow-1 me-2'
        style={group.quoteStatus?.color ? {paddingLeft: '5px'} : undefined}
      >
        <span className='fw-bolder text-gray-800 fs-6'>{group.quoteStatus?.name || '—'}</span>
      </div>
    </div>
  )
}

function ChildQuoteStatusCell({group, statusLabel}: {group: any; statusLabel: string}) {
  return (
    <div className='d-flex align-items-center rounded p-5'>
      {group.quoteStatus?.color && (
        <span
          className='bullet bullet-vertical h-30px'
          style={{background: group.quoteStatus.color}}
        />
      )}
      <div
        className='flex-grow-1 me-2'
        style={group.quoteStatus?.color ? {paddingLeft: '5px'} : undefined}
      >
        <span className='fw-bolder text-gray-800 fs-6'>{statusLabel}</span>
      </div>
    </div>
  )
}

type RollupTotals = {
  totalCost: number
  totalSell: number
  margin: number
  marginPct: number | null
}

function RollupMetric({label, children}: {label: string; children: React.ReactNode}) {
  return (
    <div className='text-end' style={{minWidth: '5.25rem'}}>
      <div className='text-muted fw-bold fs-7 text-uppercase'>{label}</div>
      <div className='fw-bolder fs-6 text-gray-800 text-nowrap'>{children}</div>
    </div>
  )
}

function ProjectCostRollupRow({
  title,
  totals,
  bulletColor,
  emphasis = false,
}: {
  title: string
  totals: RollupTotals
  bulletColor?: string
  emphasis?: boolean
}) {
  const shell = emphasis
    ? 'border border-primary border-dashed rounded-3 bg-light-primary px-4 py-3'
    : 'border border-gray-300 border-dashed rounded-3 bg-body px-4 py-3'

  return (
    <div className={shell}>
      <div className='row g-3 align-items-center'>
        <div className='col-12 col-md-auto'>
          <div className='d-flex align-items-center'>
            {bulletColor ? (
              <span
                className='bullet bullet-vertical h-24px me-3 flex-shrink-0'
                style={{background: bulletColor}}
              />
            ) : null}
            <span className={`fw-bold text-gray-800 ${emphasis ? 'fs-5' : 'fs-6'}`}>{title}</span>
          </div>
        </div>
        <div className='col-12 col-md'>
          <div className='d-flex flex-wrap justify-content-md-end gap-4 gap-md-5'>
            <RollupMetric label='Cost'>{toMoneyFormat(totals.totalCost)}</RollupMetric>
            <RollupMetric label='Sell'>{toMoneyFormat(totals.totalSell)}</RollupMetric>
            <RollupMetric label='Margin'>{toMoneyFormat(totals.margin)}</RollupMetric>
            <RollupMetric label='Margin %'>{formatMarginPercent(totals.marginPct)}</RollupMetric>
          </div>
        </div>
      </div>
    </div>
  )
}

const WorkOrderProjectCostModal: React.FC<Props> = (props) => {
  const {
    handleClose,
    show,
    parentWorkOrderId,
    parentWorkOrderDisplay,
    updateParentProjectCost,
    refreshData,
  } = props

  const [loading, setLoading] = useState(false)
  const [groups, setGroups] = useState<any[]>([])
  const [fullQuotes, setFullQuotes] = useState<any[]>([])
  const [chargesList, setChargesList] = useState<any[]>([])

  const parentIdStr = parentWorkOrderId != null ? String(parentWorkOrderId) : ''

  const fullQuoteById = useMemo(() => {
    const m = new Map<string, any>()
    for (const q of fullQuotes) {
      if (q?.id != null) m.set(String(q.id), q)
    }
    return m
  }, [fullQuotes])

  // Intentionally omit updateParentProjectCost from deps: parent passes an inline callback each render.
  useEffect(() => {
    if (!parentWorkOrderId) return

    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const bundle = await fetchParentWorkOrderQuotesBundle(parentWorkOrderId)
        const list = bundle.groups
        const fullById = new Map<string, any>()
        for (const q of bundle.fullQuotes) {
          if (q?.id != null) fullById.set(String(q.id), q)
        }
        const approvedSell = totalForStatusGroup(
          list,
          'approved',
          fullById,
          bundle.chargesList,
          parentWorkOrderId != null ? String(parentWorkOrderId) : '',
          'sell'
        )

        if (!cancelled && bundle.successful) {
          setGroups(list)
          setFullQuotes(bundle.fullQuotes)
          setChargesList(bundle.chargesList)
          updateParentProjectCost?.(approvedSell)
        } else if (!cancelled && !bundle.successful) {
          setGroups([])
          setFullQuotes([])
          setChargesList([])
          updateParentProjectCost?.(0)
        }
      } catch {
        if (!cancelled) {
          setGroups([])
          setFullQuotes([])
          setChargesList([])
          updateParentProjectCost?.(0)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateParentProjectCost is unstable from parent
  }, [parentWorkOrderId, refreshData])

  const submittedGroup = groups.find(
    (g) => (g.quoteStatus?.name || '').toLowerCase() === 'submitted'
  )
  const approvedGroup = groups.find((g) => (g.quoteStatus?.name || '').toLowerCase() === 'approved')
  const cancelledGroup = groups.find(
    (g) => (g.quoteStatus?.name || '').toLowerCase() === 'cancelled'
  )

  const allApprovedQuotes = useMemo(() => {
    if (!approvedGroup) return []
    return quotesForGroup(approvedGroup).map((q) => fullQuoteById.get(String(q.id)) ?? q)
  }, [approvedGroup, fullQuoteById])

  const childSystemFeeRows = useMemo(
    () => collectChildSystemFeeChargeRows(allApprovedQuotes, chargesList, parentIdStr),
    [allApprovedQuotes, chargesList, parentIdStr]
  )

  const childSystemFeesSubtotal = useMemo(
    () => childSystemFeeRows.reduce((s, row) => s + (Number(row.charge?.value) || 0), 0),
    [childSystemFeeRows]
  )

  const chargesBandRowCount = childSystemFeeRows.length

  const showChargesBand = childSystemFeeRows.length > 0

  const submittedTotals = useMemo(() => {
    if (!submittedGroup) {
      return {totalCost: 0, totalSell: 0, margin: 0, marginPct: null as number | null}
    }
    const {cost, sell} = sumGroupCostSellAligned(
      submittedGroup,
      fullQuoteById,
      chargesList,
      parentIdStr
    )
    const {lineCost, lineSell} = sumGroupLineCostSell(
      submittedGroup,
      fullQuoteById,
      chargesList,
      parentIdStr
    )
    return {totalCost: cost, totalSell: sell, ...computeMargin(lineCost, lineSell)}
  }, [submittedGroup, fullQuoteById, chargesList, parentIdStr])

  const approvedTotals = useMemo(() => {
    if (!approvedGroup) {
      return {totalCost: 0, totalSell: 0, margin: 0, marginPct: null as number | null}
    }
    const {cost, sell} = sumGroupCostSellAligned(
      approvedGroup,
      fullQuoteById,
      chargesList,
      parentIdStr
    )
    const {lineCost, lineSell} = sumGroupLineCostSell(
      approvedGroup,
      fullQuoteById,
      chargesList,
      parentIdStr
    )
    return {totalCost: cost, totalSell: sell, ...computeMargin(lineCost, lineSell)}
  }, [approvedGroup, fullQuoteById, chargesList, parentIdStr])

  const cancelledTotals = useMemo(() => {
    if (!cancelledGroup || quotesForGroup(cancelledGroup).length === 0) {
      return {totalCost: 0, totalSell: 0, margin: 0, marginPct: null as number | null}
    }
    const {cost, sell} = sumGroupCostSellWithCharges(
      cancelledGroup,
      fullQuoteById,
      chargesList,
      parentIdStr
    )
    const {lineCost, lineSell} = sumGroupLineCostSell(
      cancelledGroup,
      fullQuoteById,
      chargesList,
      parentIdStr
    )
    return {totalCost: cost, totalSell: sell, ...computeMargin(lineCost, lineSell)}
  }, [cancelledGroup, fullQuoteById, chargesList, parentIdStr])

  const grandTotals = useMemo(() => {
    const nonCancelled = groups.filter(
      (g) => (g.quoteStatus?.name || '').toLowerCase() !== 'cancelled'
    )
    const {
      cost: totalCost,
      sell: totalSell,
      lineCost: grandLineCost,
      lineSell: grandLineSell,
    } = nonCancelled.reduce(
      (acc, group) => {
        const statusLower = (group.quoteStatus?.name || '').toLowerCase()
        const useAligned = statusLower === 'submitted' || statusLower === 'approved'
        const {cost, sell} = useAligned
          ? sumGroupCostSellAligned(group, fullQuoteById, chargesList, parentIdStr)
          : sumGroupCostSellWithCharges(group, fullQuoteById, chargesList, parentIdStr)
        const {lineCost, lineSell} = sumGroupLineCostSell(
          group,
          fullQuoteById,
          chargesList,
          parentIdStr
        )
        return {
          cost: acc.cost + cost,
          sell: acc.sell + sell,
          lineCost: acc.lineCost + lineCost,
          lineSell: acc.lineSell + lineSell,
        }
      },
      {cost: 0, sell: 0, lineCost: 0, lineSell: 0}
    )
    return {
      totalCost,
      totalSell,
      ...computeMargin(grandLineCost, grandLineSell),
    }
  }, [groups, fullQuoteById, chargesList, parentIdStr])

  return (
    <>
      <Modal isOpen={show} onHide={() => {}} zIndex={1000} size='xl'>
        <ModalHeader closeButton>
          <h5 className='modal-title'>Project Cost </h5>
        </ModalHeader>
        <ModalBody className='p-0'>
          <div className='card-body pt-6 px-6 pb-4' style={{maxHeight: '800px', overflow: 'auto'}}>
            {!loading && groups.length > 0 && showChargesBand && (
              <div className='border-top border-gray-300 px-6 py-4' style={{background: '#eef0f7'}}>
                <div
                  className='fw-bolder text-gray-600 text-uppercase fs-7 mb-3'
                  style={{letterSpacing: '0.02em'}}
                >
                  System / tenant fees
                </div>
                <div className='border border-gray-300 border-dashed rounded-3 bg-body'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mb-0'>
                    <tbody>
                      {childSystemFeeRows.map(({charge, workOrderLabel}) => {
                        const v = Number(charge?.value) || 0
                        const rowKey =
                          charge?.id != null
                            ? `child-fee-${charge.id}`
                            : `child-fee-${workOrderLabel}-${String(charge?.name)}-${v}`
                        return (
                          <tr key={rowKey}>
                            <td className='text-gray-700 ps-4 py-2'>
                              {charge.chargeType?.name ?? chargeRowLabel(charge)}
                            </td>
                            <td className='text-muted fs-7 py-2'>Work order {workOrderLabel}</td>
                            <td className='text-end fw-bolder text-gray-800 pe-4 py-2 text-nowrap'>
                              {toMoneyFormat(v)}
                            </td>
                          </tr>
                        )
                      })}
                      {chargesBandRowCount > 1 && (
                        <tr className='border-top border-gray-200'>
                          <td colSpan={2} className='text-end text-muted fw-bold fs-7 py-2 pe-3'>
                            Subtotal
                          </td>
                          <td className='text-end fw-bolder text-gray-800 pe-4 py-2 text-nowrap'>
                            {toMoneyFormat(childSystemFeesSubtotal)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <table className='table align-middle gs-0 gy-4'>
              <thead>
                <tr className='text-start text-gray-400 fw-bolder fs-7 text-uppercase gs-0'>
                  <th className='min-w-90px'>Project</th>
                  <th className='min-w-120px'>Status</th>
                  <th className='min-w-90px text-end'>Cost</th>
                  <th className='min-w-90px text-end'>Sell/charge</th>
                  <th className='min-w-70px text-end'>Margin</th>
                  <th className='min-w-70px text-end'>Margin%</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={TABLE_COL_SPAN}>
                      <span className='indicator-progress' style={{display: 'block'}}>
                        Loading...
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    </td>
                  </tr>
                )}
                {!loading &&
                  groups.map((group, groupIndex) => {
                    const rowKey = expandGroupKey(group, groupIndex)
                    const quotes = quotesForGroup(group)
                    const parentSellChargeSum = sumGroupParentWoChargeSell(
                      group,
                      fullQuoteById,
                      chargesList,
                      parentIdStr
                    )
                    return (
                      <React.Fragment key={rowKey}>
                        <tr>
                          <td className='text-muted fs-7'>{parentWorkOrderDisplay ?? '—'}</td>
                          <td>
                            <GroupStatusCell group={group} />
                          </td>
                          <td className='text-end fw-bolder fs-6 text-dark'>—</td>
                          <td className='text-end fw-bolder fs-6 text-dark'>
                            {parentSellChargeSum != null ? toMoneyFormat(parentSellChargeSum) : '—'}
                          </td>
                          <td className='text-end fw-bolder fs-6 text-dark'>—</td>
                          <td className='text-end fw-bolder fs-6 text-dark'>
                            {formatMarginPercent(null)}
                          </td>
                        </tr>
                        {quotes.map((quote: any, idx: number) => {
                          const full = fullQuoteById.get(String(quote.id)) ?? quote
                          const {cost: quoteCost, sell: quoteSell} = quoteChildWorkOrderTotals(
                            full,
                            chargesList,
                            parentIdStr,
                            group.quoteStatus?.name
                          )
                          const quoteMargin = computeMargin(quoteCost, quoteSell)
                          const statusLabel =
                            quote.quoteStatus?.name || group.quoteStatus?.name || '—'

                          return (
                            <tr className='bg-light' key={`g-${rowKey}-q-${quote.id ?? idx}`}>
                              <td className='bg-light text-dark fw-bolder fs-6'>
                                {quoteWorkOrderLabel(full)}
                              </td>
                              <td className='bg-light'>
                                <ChildQuoteStatusCell group={group} statusLabel={statusLabel} />
                              </td>
                              <td className='bg-light text-end fw-bolder fs-6 text-dark'>
                                {toMoneyFormat(quoteCost)}
                              </td>
                              <td className='bg-light text-end fw-bolder fs-6 text-dark'>
                                {toMoneyFormat(quoteSell)}
                              </td>
                              <td className='bg-light text-end fw-bolder fs-6 text-dark'>
                                {toMoneyFormat(quoteMargin.margin)}
                              </td>
                              <td className='bg-light text-end fw-bolder fs-6 text-dark'>
                                {formatMarginPercent(quoteMargin.marginPct)}
                              </td>
                            </tr>
                          )
                        })}
                        {quotes.length === 0 && (
                          <tr className='bg-light'>
                            <td colSpan={TABLE_COL_SPAN} className='bg-light text-muted fs-7'>
                              No quotes in this group.
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                {!loading && groups.length === 0 && (
                  <tr>
                    <td colSpan={TABLE_COL_SPAN} className='text-muted fs-7'>
                      No project cost breakdown for this work order.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!loading && groups.length > 0 && (
            <div className='border-top border-gray-300 bg-light px-6 py-5'>
              <div className='text-gray-600 fw-bolder text-uppercase fs-7 mb-2'>Roll-up totals</div>
              <div className='d-flex flex-column gap-3'>
                <ProjectCostRollupRow
                  title='Submitted total'
                  totals={submittedTotals}
                  bulletColor={submittedGroup?.quoteStatus?.color ?? '#009ef7'}
                />
                <ProjectCostRollupRow
                  title='Approved total'
                  totals={approvedTotals}
                  bulletColor={approvedGroup?.quoteStatus?.color ?? '#50cd89'}
                />
                {cancelledGroup && quotesForGroup(cancelledGroup).length > 0 && (
                  <ProjectCostRollupRow
                    title='Cancelled total'
                    totals={cancelledTotals}
                    bulletColor={cancelledGroup?.quoteStatus?.color ?? '#7e8299'}
                  />
                )}
                <ProjectCostRollupRow
                  title='Totals'
                  totals={grandTotals}
                  bulletColor='#009ef7'
                  emphasis
                />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter className='d-flex justify-content-end align-items-center flex-wrap gap-3'>
          <button type='button' onClick={() => handleClose()} className='btn btn-light'>
            Close
          </button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default WorkOrderProjectCostModal
